import { reactive, nextTick } from 'vue'
import type { Node, Edge } from '@vue-flow/core'
import { cloneDeep } from 'es-toolkit/compat'

// 类型定义
type Position = {
  x: number
  y: number
}

interface FlowNode extends Node {
  label: string
  type: string
}

interface WorkflowState {
  nodes: FlowNode[]          // 节点列表
  edges: Edge[]              // 连线列表
  selectedNodes: FlowNode[]  // 已选中的节点
  activeNodeId: string | null // 当前激活的节点ID
  snapshots: FlowNode[][]    // 历史快照
  currentIndex: number       // 当前快照索引
  currentWorkflowId: string | null // 当前加载的工作流ID
}

// 将原本匿名的返回类型提取为命名类型，便于在单例中复用
export type WorkflowStoreApi = {
  state: WorkflowState,
  setCurrentWorkflowId: (id: string | null) => void,
  addNode: (node: FlowNode) => void,
  findNode: (nodeId: string) => FlowNode | undefined,
  updateNode: (nodeId: string, updates: Partial<FlowNode>) => void,
  updateNodeData: (nodeId: string, dataUpdates: Partial<FlowNode['data']>) => void,
  // 新增：实时预览用，仅更新 propertyPanel，不保存快照
  updateNodePropertyPanel: (nodeId: string, propertyPanel: any) => void,
  moveNode: (nodeId: string, position: Position) => void,
  removeNode: (nodeId: string) => void,
  addEdge: (edge: Edge) => void,
  updateEdges: (newEdges: Edge[]) => void,
  removeEdge: (edgeId: string) => void,
  selectNode: (node: FlowNode) => void,
  clearSelection: () => void,
  saveSnapshot: () => void,
  undo: () => void,
  redo: () => void,
  importWorkflow: (nodes: FlowNode[], edges: Edge[]) => void,
  exportWorkflow: () => { nodes: FlowNode[]; edges: Edge[] },
  clearWorkflow: () => void,
  setActiveNodeId: (nodeId: string | null) => void
}

// 单例缓存，确保多处 useWorkflowStore() 获取同一份状态
let __workflowStore__: WorkflowStoreApi | null = null

export function useWorkflowStore(): WorkflowStoreApi {
  if (__workflowStore__) return __workflowStore__

  const state = reactive<WorkflowState>({
    nodes: [],
    edges: [],
    selectedNodes: [],
    activeNodeId: null,
    snapshots: [],
    currentIndex: -1,
    currentWorkflowId: null
  })

  // 设置当前工作流ID
  function setCurrentWorkflowId(id: string | null) {
    state.currentWorkflowId = id
  }

  // 节点操作
  function addNode(node: FlowNode) {
    state.nodes.push(node)
    // 添加节点
    saveSnapshot()
  }

  function findNode(nodeId: string) {
    return state.nodes.find(node => node.id === nodeId)
  }

  function updateNode(nodeId: string, updates: Partial<FlowNode>) {
    const node = findNode(nodeId)
    if (node) {
      Object.assign(node, updates)
      saveSnapshot()
    }
  }

  function updateNodeData(nodeId: string, dataUpdates: Partial<FlowNode['data']>) {
    const node = findNode(nodeId)
    if (node && node.data) {
      Object.assign(node.data, dataUpdates)
      saveSnapshot()
    }
  }

  // 新增：实时预览更新 propertyPanel，不保存快照
  function updateNodePropertyPanel(nodeId: string, propertyPanel: any) {
    const node = findNode(nodeId)
    if (node) {
      if (!node.data) (node as any).data = {}
      ;(node.data as any).propertyPanel = cloneDeep(propertyPanel)
    }
  }

  function moveNode(nodeId: string, position: Position) {
    updateNode(nodeId, { position })
  }

  function removeNode(nodeId: string) {
    const nodeIndex = state.nodes.findIndex(node => node.id === nodeId)
    if (nodeIndex !== -1) {
      state.nodes.splice(nodeIndex, 1)
    }
    state.edges = state.edges.filter(
      edge => edge.source !== nodeId && edge.target !== nodeId
    )
    saveSnapshot()
  }

  // 连线操作
  function addEdge(edge: Edge) {
    console.log(edge.id)
    state.edges.push(edge)
    saveSnapshot()
  }

  function updateEdges(newEdges: Edge[]) {
    state.edges.splice(0, state.edges.length, ...newEdges)
    saveSnapshot()
  }

  function removeEdge(edgeId: string) {
    const edgeIndex = state.edges.findIndex(edge => edge.id === edgeId)
    if (edgeIndex !== -1) {
      state.edges.splice(edgeIndex, 1)
    }
    saveSnapshot()
  }

  // 选择操作
  function selectNode(node: FlowNode) {
    state.selectedNodes = [node]
    state.activeNodeId = node.id
  }

  function clearSelection() {
    state.selectedNodes = []
    state.activeNodeId = null
  }

  // 历史记录操作
  function saveSnapshot() {
    if (state.currentIndex < state.snapshots.length - 1) {
      state.snapshots = state.snapshots.slice(0, state.currentIndex + 1)
    }
    state.snapshots.push(cloneDeep(state.nodes))
    state.currentIndex++
  }

  function undo() {
    if (state.currentIndex > 0) {
      state.currentIndex--
      state.nodes = cloneDeep(state.snapshots[state.currentIndex])
    }
  }

  function redo() {
    if (state.currentIndex < state.snapshots.length - 1) {
      state.currentIndex++
      state.nodes = cloneDeep(state.snapshots[state.currentIndex])
    }
  }

  // 导入导出功能
  function importWorkflow(nodes: FlowNode[], edges: Edge[]) {
    clearWorkflow()
    
    const clonedNodes = cloneDeep(nodes || [])
    const nodeIds = new Set(clonedNodes.map((n: any) => n.id))
    const filteredEdges = (edges || []).filter((e: any) => nodeIds.has(e.source) && nodeIds.has(e.target))

    // 重置历史并批量替换：先设节点，延后一拍再设边，确保 VueFlow 节点与把手已完成挂载
    state.snapshots = []
    state.currentIndex = -1
    state.nodes = clonedNodes

    // 等待 2 次 nextTick：
    // 1) 让 VueFlow 根据 nodes 渲染节点容器
    // 2) 让节点内部根据配置生成把手（Handle），避免边因找不到把手而被忽略
    Promise.resolve()
      .then(() => nextTick())
      .then(() => nextTick())
      .then(() => {
        state.edges = cloneDeep(filteredEdges)
        saveSnapshot()
      })
  }

  function exportWorkflow() {
    return {
      nodes: cloneDeep(state.nodes),
      edges: cloneDeep(state.edges)
    }
  }

  // 清空当前工作流
  function clearWorkflow() {
    if (state.nodes.length > 0) {
      state.nodes = []
    }
    if (state.edges.length > 0) {
      state.edges = []
    }
    state.selectedNodes = []
    state.activeNodeId = null
    state.snapshots = []
    state.currentIndex = -1
  }

  // 设置激活节点ID
  function setActiveNodeId(nodeId: string | null) {
    state.activeNodeId = nodeId
  }

  __workflowStore__ = {
    state,
    setCurrentWorkflowId,
    addNode,
    findNode,
    updateNode,
    updateNodeData,
    // 新增暴露：实时预览 propertyPanel
    updateNodePropertyPanel,
    moveNode,
    removeNode,
    addEdge,
    updateEdges,
    removeEdge,
    selectNode,
    clearSelection,
    saveSnapshot,
    undo,
    redo,
    importWorkflow,
    exportWorkflow,
    clearWorkflow,
    setActiveNodeId
  }

  return __workflowStore__
}
